import { FC, useState } from 'react';
import { CompanyAWS } from '../../../@types/AWS';
import Modal from '../../common/Modal/Modal';
import Form from '../../common/Form/Form';
import { FormProvider, useFieldArray, useFormContext } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import axios from '../../../api/axios';
import { CompanyAttributes, License, LicenseStatusTypes, LicenseTypes } from '../../../@types/Company';
import Shape from '../../../@types/Shape';
import USStates from '../../../data/USStates';
import { StateFieldAutoComplete } from '../../common/Form/FormFields/Autocomplete/Autocomplete';
import CollapsibleSection from '../../common/Form/CollapsibleSection/CollapsibleSection';
import { useConfirmationModalContext } from '../../../context/ConfirmModalContext';
import { IconButton, Stack } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import LoadingButton from '../../common/LoadingButton/LoadingButton';
import Button from '../../common/Button';
import DatePicker from '../../common/Form/FormFields/DatePicker/DatePicker';
import SelectOptions from '../../common/Form/FormFields/Select/Select';
import SelectOption from '../../../@types/SelectOption';
import flatten from 'flat';

interface CreateLicenseModalProps {
  company: CompanyAWS;
  closeModal: () => void;
  isVisible: boolean;
}

const CreateLicenseModal: FC<CreateLicenseModalProps> = ({ company, closeModal, isVisible }) => {
  const [loading, setLoading] = useState<boolean>(false);

  const onSubmit = async (newLicenseData: any) => {
    try {
      setLoading(true);
      const licenses: License[] = [...newLicenseData.licenses, ...company.attributes.licenses ?? []];
      const newCompanyAttributesList: CompanyAttributes = { ...company.attributes, licenses: licenses }
      const updatedCompany: CompanyAWS = { ...company, attributes: newCompanyAttributesList };
      await axios.put(`/dealer/meta-data`, updatedCompany);
      toast.success('New licenses created!');
      closeModal();
    } catch (error: any) {
      console.log(error);
      toast.error('Server Error');
    } finally {
      setLoading(false);
    }
  };

  const onError = (errors: any) => {
    console.log(errors);
    const errorsFlattened: any = flatten(errors);
    const requiredErrors = Object.fromEntries(Object.entries(errorsFlattened).filter(([key, value]) => key.includes('.type')));
    if (Object.entries(requiredErrors).length <= 2) {
      for (const key in requiredErrors) {
        let keySplit = key.split('.');
        keySplit.pop();
        let formField = '';
        for (const fieldPart of keySplit) {
          formField += fieldPart.split(/([A-Z][a-z]+)/).map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
        }
        toast.error(`Please check [${formField.trim()}] field for errors`);
      }
    } else {
      toast.error('Please check form for errors');
    }
  };

  const createLicenseSchema = yup.object({
    licenses: yup.array().of(
      yup.object<Shape<License>>({
        status: yup.string().required().default(LicenseStatusTypes[0]),
        stateCode: yup.string().required(),
        state: yup.string().required(),
        type: yup.string().required(),
        expirationDate: yup.string().required()
      })
    ).min(1, 'Please add a minimum of one license')
  })

  return (
    <>
      {isVisible &&
        <Modal title="Create Licenses" closeModal={closeModal}>
          <Form onSubmit={onSubmit} onError={onError} resolver={yupResolver(createLicenseSchema)} keepValuesOnReset={true}>
            {(methods) => (
              <FormProvider {...methods}>
                <Stack direction="column" spacing={2}>
                  <LicenseArray name="licenses" label="Licenses" />
                  <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <LoadingButton loading={loading} type="submit">Submit</LoadingButton>
                    <Button onClick={closeModal} type="button">Cancel</Button>
                  </Stack>
                </Stack>
              </FormProvider>
            )}
          </Form>
        </Modal>
      }
    </>
  );
}

interface LicenseArrayProps {
  name: string,
  label: string,
}

const LicenseArray: FC<LicenseArrayProps> = ({ name, label }) => {
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name: name });

  const confirmContext = useConfirmationModalContext();

  if (!fields.length) append({ name: name });

  // State management for expanding all collapsible sections
  const [expandAll, setExpandAll] = useState<boolean>(true);

  const deleteLicense = async (index: number) => {
    const result = await confirmContext.showConfirmation(
      `Delete [License #${index + 1}]`,
      `Would you like to delete [License #${index + 1}]?`
    )
    if (result) remove(index);
  }

  const addLicense = () => {
    append({ name: '2' });
  }

  const getLicenseOptions = (): SelectOption[] => {
    const options: SelectOption[] = [];
    LicenseTypes.forEach((license) => {
      options.push({ label: `${license} License`, value: license });
    });
    return options;
  }

  return (
    <Stack
      direction="column"
      spacing={4}
      sx={{ paddingBottom: '36px' }}>
      {fields.map((item, index) => {
        const formFieldIndex = `${name}.${index}`;
        return (
          <Stack
            key={index}
            direction="row"
            spacing={2}
            alignItems="baseline">
            <CollapsibleSection
              heading={`Licenses #${index + 1}`}
              expandAll={expandAll}
              setExpandAll={setExpandAll}>
              <StateFieldAutoComplete
                options={USStates}
                name={`${formFieldIndex}.state`}
                label={'State'}
                allowUserInput={true} />
              <SelectOptions
                name={`${formFieldIndex}.type`}
                label="Type"
                options={getLicenseOptions()} />
              <DatePicker
                name={`${formFieldIndex}.expirationDate`}
                label="Expiration Date" />
            </CollapsibleSection>
            <Stack
              direction="row"
              justifyContent="center">
              <IconButton
                aria-label="delete"
                onClick={() => deleteLicense(index)}
                style={{ marginTop: '40px' }}>
                <DeleteIcon />
              </IconButton>
            </Stack>
          </Stack>
        )
      })}
      <Stack
        direction="row"
        justifyContent="center" >
        <Button onClick={addLicense}>Add Additional License</Button>
      </Stack>
    </Stack>
  )

}

export default CreateLicenseModal;

